/**
 * AUI Node Renderer
 *
 * Recursively renders a node from the flat AUI component tree.
 * Looks up the component type in the registry, renders children by ID reference.
 */

import { componentRegistry } from './registry';
import { AuiFallback } from './components/AuiFallback';
import type { AuiNode, AuiAction } from '../../types/canvases';

interface AuiNodeRendererProps {
  nodeId: string;
  nodeMap: Map<string, AuiNode>;
  onAction?: (action: AuiAction) => Promise<void>;
}

export function AuiNodeRenderer({ nodeId, nodeMap, onAction }: AuiNodeRendererProps) {
  const node = nodeMap.get(nodeId);
  if (!node) return null;

  const Comp = componentRegistry[node.component.type] ?? AuiFallback;

  const childIds = node.component.children;
  const children = childIds?.map((childId) => (
    <AuiNodeRenderer
      key={childId}
      nodeId={childId}
      nodeMap={nodeMap}
      onAction={onAction}
    />
  ));

  const childNodes = childIds
    ?.map((childId) => nodeMap.get(childId))
    .filter((n): n is AuiNode => n !== undefined);

  return (
    <Comp node={node} onAction={onAction} childNodes={childNodes}>
      {children}
    </Comp>
  );
}
